import type { CostingTemplateDetail } from './store/productCosting.types';
import type { SpecificationRequirementRow } from './types';

/**
 * Pure logic for the Spec Requirements section (BM-9899).
 *
 * Kept out of the component and the page hook for the same reason the landed and exchange-rate maths
 * are: this is the part with real edge cases, and it is the only part that can be tested without
 * standing up the provider, redux and the grid.
 */

/**
 * Trims and lower-cases a requirement name for matching.
 *
 * Seeded rows are reconciled against the template by NAME rather than by id, because a costing row is
 * a copy — it has no pointer back to the template entry it came from. Matching leniently keeps a row
 * attached when the template fixes casing or stray whitespace.
 * @param requirement the raw requirement text.
 * @returns the comparison key, or an empty string when there is nothing to match on.
 */
export const normaliseRequirement = (requirement: unknown): string =>
  typeof requirement === 'string' ? requirement.trim().toLowerCase() : '';

/**
 * Reads the requirement names a costing template declares.
 * @param template the live costing template.
 * @returns the declared requirement names, blanks dropped.
 */
export const templateRequirementNames = (template: CostingTemplateDetail | null | undefined): string[] =>
  (template?.specificationRequirementInfo ?? []).map((entry) => (typeof entry?.title === 'string' ? entry.title : '')).filter((title) => title.trim() !== '');

/**
 * Rebuilds the section from the costing template while keeping what the user typed.
 *
 * Mirrors `handleAdditonalComponentModule`'s contract for the sibling sections: the template owns
 * which rows exist, and the costing owns the values in them. Three rules fall out of that:
 *
 *  - a template row keeps its existing `specification` when the same requirement is already on screen,
 *    so a rebuild after a refresh or a template swap does not wipe answers the template still asks for;
 *  - a template row the costing has never seen arrives blank;
 *  - rows the user added themselves are **not** template-owned, so they survive the rebuild untouched
 *    and are appended after the seeded block, which is where the Figma shows them.
 *
 * A seeded row the user deleted therefore comes back on a rebuild — that is the agreed behaviour, and
 * the same as Additional Components.
 * @param template the live costing template.
 * @param currentRows the rows currently in state, seeded and user-added mixed.
 * @returns the rebuilt row list: seeded rows in template order, then the user's own.
 */
export const mergeTemplateRequirements = (
  template: CostingTemplateDetail | null | undefined,
  currentRows: SpecificationRequirementRow[] | undefined,
): SpecificationRequirementRow[] => {
  const rows = Array.isArray(currentRows) ? currentRows : [];

  const answeredByRequirement = new Map<string, unknown>();
  rows.forEach((row) => {
    const key = normaliseRequirement(row?.requirement);
    if (!key || answeredByRequirement.has(key)) return;
    answeredByRequirement.set(key, row?.specification);
  });

  const seeded: SpecificationRequirementRow[] = templateRequirementNames(template).map((requirement) => ({
    requirement,
    specification: answeredByRequirement.get(normaliseRequirement(requirement)) ?? '',
    isFromTemplate: true,
  }));

  const seededKeys = new Set(seeded.map((row) => normaliseRequirement(row.requirement)));
  /* A user row whose name now collides with a template row is dropped rather than duplicated: the
     template entry has taken ownership of that requirement, and its value was carried over above. */
  const userAdded = rows.filter((row) => !row?.isFromTemplate && !seededKeys.has(normaliseRequirement(row?.requirement)));

  return [...seeded, ...userAdded];
};

/**
 * Drops rows that were never filled in, ready for the save payload.
 *
 * The grid always shows a spare blank row so there is somewhere to type and something for the `+` to
 * sit on, and a user can add rows and then leave them alone. None of those belong in the document.
 * A blank `requirement` is the test: it is the row's identity, so a specification typed against no
 * requirement records nothing and a seeded row can never have one.
 * @param rows every row currently in state.
 * @returns only the rows worth persisting.
 */
export const withoutEmptyRequirements = (rows: SpecificationRequirementRow[] | undefined): SpecificationRequirementRow[] =>
  (Array.isArray(rows) ? rows : []).filter((row) => String(row?.requirement ?? '').trim() !== '');

/** A blank row for the `+` action. User-added, so both of its cells are editable. */
export const blankSpecificationRequirementRow = (): SpecificationRequirementRow => ({
  requirement: '',
  specification: '',
  isFromTemplate: false,
});
